
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useUser } from '@/hooks/auth/use-user';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/errors/use-toast';
import { updateUser } from '@/hooks/auth/database-users';

const formSchema = z.object({
  firstName: z.string().min(2, {
    message: "First name must be at least 2 characters.",
  }),
  lastName: z.string().min(2, {
    message: "Last name must be at least 2 characters.",
  }),
  username: z.string()
    .min(3, { message: "Username must be at least 3 characters." })
    .max(50, { message: "Username must be at most 50 characters." })
    .regex(/^[a-zA-Z0-9_-]+$/, { message: "Username can only contain letters, numbers, underscores, and hyphens." })
    .optional()
    .or(z.literal(""))
})

const PersonalInformationForm = () => {
  const { user, loading } = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      username: "",
    },
  })
  
  // Update form when user data is loaded
  useEffect(() => {
    const loadUserData = async () => {
      if (user?.id) {
        try {
          // Get user data from users table
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('first_name, last_name')
            .eq('id', user.id)
            .single();

          // Get username from public_profile table
          const { data: profileData, error: profileError } = await supabase
            .from('public_profile')
            .select('username')
            .eq('id', user.id)
            .single();

          if (userError && userError.code !== 'PGRST116') {
            console.error('Error loading user data:', userError);
          }

          if (profileError && profileError.code !== 'PGRST116') {
            console.error('Error loading profile data:', profileError);
          }

          form.reset({
            firstName: userData?.first_name || '',
            lastName: userData?.last_name || '',
            username: profileData?.username || ''
          });
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      }
    };

    if (user?.id && !loading) {
      loadUserData();
    }
  }, [user?.id, loading, form]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (!user?.id) return;
    
    try {
      setIsSubmitting(true);
      
      // Update users table
      const { error: userError } = await updateUser(user.id, {
        first_name: data.firstName,
        last_name: data.lastName
      });

      if (userError) {
        throw new Error(`Failed to update user data: ${userError.message}`);
      }

      // Update public_profile table (upsert to handle cases where profile doesn't exist)
      const { error: profileError } = await supabase
        .from('public_profile')
        .upsert({
          id: user.id,
          first_name: data.firstName,
          username: data.username || null,
          updated_at: new Date().toISOString()
        });

      if (profileError) {
        throw new Error(`Failed to update profile: ${profileError.message}`);
      }

      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
        variant: "default"
      });

      // Dispatch custom event to notify other components
      const event = new CustomEvent('profile-updated', {
        detail: { userId: user.id }
      });
      document.dispatchEvent(event);

    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Update failed",
        description: error instanceof Error ? error.message : "Failed to update profile",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username (Public)</FormLabel>
                <FormControl>
                  <Input placeholder="Choose a username" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name (Public)</FormLabel>
                <FormControl>
                  <Input placeholder="First name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name (Private)</FormLabel>
                <FormControl>
                  <Input placeholder="Last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Button 
            type="submit" 
            className="bg-green-500 hover:bg-green-600 text-white"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Submit'}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default PersonalInformationForm;
